import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { PlanProvider } from "./components/PlanGate";
import { FeatureUpgradeProvider } from "./components/FeatureUpgradeModal";
const AdminLayout = React.lazy(() => import("./layouts/AdminLayoutNew"));
const MarketingLayout = React.lazy(() => import("./layouts/MarketingLayout"));
import { I18nProvider, I18nScope } from "./contexts/I18nContext";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
const Login = React.lazy(() => import("./pages/admin/Login"));
const TenantForgotPassword = React.lazy(() => import("./pages/admin/TenantForgotPassword"));
const TenantResetPassword = React.lazy(() => import("./pages/admin/TenantResetPassword"));
const AuthLoginRedirect = React.lazy(() => import("./pages/auth/Login"));
const AuthRegisterRedirect = React.lazy(() => import("./pages/auth/Register"));
const Dashboard = React.lazy(() => import("./pages/admin/Dashboard"));
const CategoryList = React.lazy(() => import("./pages/admin/catalog/CategoryList"));
const ItemList = React.lazy(() => import("./pages/admin/catalog/ItemList"));
const ItemDetail = React.lazy(() => import("./pages/admin/catalog/ItemDetail"));
const PackageList = React.lazy(() => import("./pages/admin/catalog/PackageList"));
const PackageDetail = React.lazy(() => import("./pages/admin/catalog/PackageDetail"));
const PackageBuilder = React.lazy(() => import("./pages/admin/catalog/PackageBuilder"));
const BookingList = React.lazy(() => import("./pages/admin/bookings/BookingList"));
const BookingDetail = React.lazy(() => import("./pages/admin/bookings/BookingDetail"));
const BookingCreatePage = React.lazy(() => import("./pages/admin/bookings/BookingCreatePage"));
const QuoteList = React.lazy(() => import("./pages/admin/bookings/QuoteList"));
const QuoteDetail = React.lazy(() => import("./pages/admin/bookings/QuoteDetail"));
const OrdersPage = React.lazy(() => import("./pages/admin/orders/OrdersPage"));
const SchedulePage = React.lazy(() => import("./pages/admin/schedule/SchedulePage"));
const JobDetail = React.lazy(() => import("./pages/admin/schedule/JobDetail"));
const StaffPage = React.lazy(() => import("./pages/admin/staff/StaffPage"));
const LegacyJobRedirect = () => { const { id } = useParams(); return <Navigate to={`/admin/schedule/jobs/${id}`} replace />; };
const FinancialDashboard = React.lazy(() => import("./pages/admin/financial/FinancialDashboard"));
const CustomerList = React.lazy(() => import("./pages/admin/customers/CustomerList"));
const CustomerDetail = React.lazy(() => import("./pages/admin/customers/CustomerDetail"));
const PromotionList = React.lazy(() => import("./pages/admin/promotions/PromotionList"));
const BannerList = React.lazy(() => import("./pages/admin/promotions/BannerList"));
const PopupList = React.lazy(() => import("./pages/admin/promotions/PopupList"));
const MarketingHub = React.lazy(() => import("./pages/admin/MarketingHub"));
const ReviewModeration = React.lazy(() => import("./pages/admin/reviews/ReviewModeration"));
const MediaLibrary = React.lazy(() => import("./pages/admin/media/MediaLibrary"));
const SettingsPage = React.lazy(() => import("./pages/admin/settings/SettingsPage"));
const FunnelList = React.lazy(() => import("./pages/admin/funnels/FunnelList"));
const FunnelDetail = React.lazy(() => import("./pages/admin/funnels/FunnelDetail"));
const InquiriesPage = React.lazy(() => import("./pages/admin/InquiriesPage"));
const UpgradePlanPage = React.lazy(() => import("./pages/admin/UpgradePlanPage"));
const TemplatesPage = React.lazy(() => import("./pages/admin/TemplatesPage"));
import { PublicSiteProvider, usePublicSite } from "./contexts/PublicSiteContext";
import { EditingMicrositeProvider } from "./contexts/EditingMicrositeContext";
const MicrositesDashboard = React.lazy(() => import("./pages/admin/microsites/MicrositesDashboard"));
const MicrositeSettings = React.lazy(() => import("./pages/admin/microsites/MicrositeSettings"));
const AdminWaivers = React.lazy(() => import("./pages/admin/settings/WaiverSettings"));
const SEOPage = React.lazy(() => import("./pages/admin/SEOPage"));
const BlogManagement = React.lazy(() => import("./pages/admin/blog/BlogManagement"));
const LocalSeoPages = React.lazy(() => import("./pages/admin/seo/LocalSeoPages"));
const CompetitorComparisonPage = React.lazy(() => import("./pages/marketing/CompetitorComparisonPage"));
const ComparisonsIndex = React.lazy(() => import("./pages/marketing/ComparisonsIndex"));
import PublicLayout from "./layouts/PublicLayout";
const MicrositeSetup = React.lazy(() => import("./pages/public/MicrositeSetup"));
const DriverLayout = React.lazy(() => import("./layouts/DriverLayout"));
const DriverLogin = React.lazy(() => import("./pages/driver/DriverLogin"));
const DriverDayView = React.lazy(() => import("./pages/driver/DriverDayView"));
const DriverJobList = React.lazy(() => import("./pages/driver/DriverJobList"));
const DriverJobDetail = React.lazy(() => import("./pages/driver/DriverJobDetail"));
const DriverHistory = React.lazy(() => import("./pages/driver/DriverHistory"));
const DriverProfile = React.lazy(() => import("./pages/driver/DriverProfile"));
import { CustomerAuthProvider, useCustomerAuth } from "./contexts/CustomerAuthContext";
const CustomerLogin = React.lazy(() => import("./pages/public/CustomerLogin"));
const EmailPreferences = React.lazy(() => import("./pages/public/EmailPreferences"));
const CustomerAccountShell = React.lazy(() => import("./pages/public/CustomerAccountShell"));
const CustomerBookings = React.lazy(() => import("./pages/public/CustomerBookings"));
const CustomerBookingDetail = React.lazy(() => import("./pages/public/CustomerBookingDetail"));
const CustomerQuotes = React.lazy(() => import("./pages/public/CustomerQuotes"));
const CustomerProfile = React.lazy(() => import("./pages/public/CustomerProfile"));
const CustomerReviewSubmit = React.lazy(() => import("./pages/public/CustomerReviewSubmit"));
const CustomerWaivers = React.lazy(() => import("./pages/public/CustomerWaivers"));
const CustomerInvoices = React.lazy(() => import("./pages/public/CustomerInvoices"));
import { PlatformAuthProvider, usePlatformAuth } from "./contexts/PlatformAuthContext";
const PlatformLayout = React.lazy(() => import("./layouts/PlatformLayout"));
const PlatformLogin = React.lazy(() => import("./pages/platform/PlatformLogin"));
const PlatformForgotPassword = React.lazy(() => import("./pages/platform/PlatformForgotPassword"));
const PlatformResetPassword = React.lazy(() => import("./pages/platform/PlatformResetPassword"));
const PlatformDashboard = React.lazy(() => import("./pages/platform/PlatformDashboard"));
const PlatformChangelog = React.lazy(() => import("./pages/platform/PlatformChangelog"));
const TenantList = React.lazy(() => import("./pages/platform/TenantList"));
const TenantDetail = React.lazy(() => import("./pages/platform/TenantDetail"));
const TenantCleanup = React.lazy(() => import("./pages/platform/TenantCleanup"));
const DemoTenants = React.lazy(() => import("./pages/platform/DemoTenants"));
const LegalSettings = React.lazy(() => import("./pages/platform/LegalSettings"));
const LegalPage = React.lazy(() => import("./pages/marketing/LegalPage"));
const PlanManagement = React.lazy(() => import("./pages/platform/PlanManagement"));
const PlanMatrixEditor = React.lazy(() => import("./pages/platform/PlanMatrixEditor"));
const ThemeManagement = React.lazy(() => import("./pages/platform/ThemeManagement"));
const StarterPackManagement = React.lazy(() => import("./pages/platform/StarterPackManagement"));
const CommunityModerationQueue = React.lazy(() => import("./pages/platform/CommunityModerationQueue"));
const IntegrationsSettings = React.lazy(() => import("./pages/platform/IntegrationsSettings"));
const PlatformAnalytics = React.lazy(() => import("./pages/platform/PlatformAnalytics"));
const AIAnalytics = React.lazy(() => import("./pages/platform/AIAnalytics"));
const PlatformAlerts = React.lazy(() => import("./pages/platform/PlatformAlerts"));
const TenantPreviewRedirect = React.lazy(() => import("./pages/platform/TenantPreviewRedirect"));
const PreviewV2Page = React.lazy(() => import("./pages/public/PreviewV2Page"));
const ThemesV2Preview = React.lazy(() => import("./pages/admin/ThemesV2Preview"));
const SiteBuilderV2 = React.lazy(() => import("./pages/admin/SiteBuilderV2"));
const SiteBuilderV2Redirect = React.lazy(() => import("./pages/admin/SiteBuilderV2Redirect"));
const TemplateGallery = React.lazy(() => import("./pages/admin/TemplateGallery"));
const CommunityTemplateGallery = React.lazy(() => import("./pages/admin/CommunityTemplateGallery"));
const PlatformActivityFeed = React.lazy(() => import("./pages/platform/PlatformActivityFeed"));
const PlatformBroadcasts = React.lazy(() => import("./pages/platform/PlatformBroadcasts"));
const WebsiteContent = React.lazy(() => import("./pages/platform/WebsiteContent"));
const ThemeMarketplace = React.lazy(() => import("./pages/platform/ThemeMarketplace"));
const PlatformInbox = React.lazy(() => import("./pages/platform/PlatformInbox"));
const CohortAnalytics = React.lazy(() => import("./pages/platform/CohortAnalytics"));
const DomainApprovals = React.lazy(() => import("./pages/platform/DomainApprovals"));
const ComparisonsManagement = React.lazy(() => import("./pages/platform/ComparisonsManagement"));
const IndexNowSettings = React.lazy(() => import("./pages/platform/IndexNowSettings"));
const TranslationPlatformSettings = React.lazy(() => import("./pages/platform/TranslationPlatformSettings"));
const AddonAnalytics = React.lazy(() => import("./pages/platform/AddonAnalytics"));
const RouteHealthPage = React.lazy(() => import("./pages/platform/RouteHealthPage"));
const SeoSetupChecklistPage = React.lazy(() => import("./pages/platform/SeoSetupChecklistPage"));
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import NotFoundCatcher from "./components/NotFoundCatcher";
import ThemeLangFab from "./components/ThemeLangFab";
const TranslationSettings = React.lazy(() => import("./pages/admin/settings/TranslationSettings"));
const MarketingFunnel = React.lazy(() => import("./pages/platform/MarketingFunnel"));
const TeamPage = React.lazy(() => import("./pages/platform/TeamPage"));
const PlatformMePage = React.lazy(() => import("./pages/platform/PlatformMePage"));
const PlatformAuditLog = React.lazy(() => import("./pages/platform/PlatformAuditLog"));
const PlatformPayments = React.lazy(() => import("./pages/platform/PlatformPayments"));
const SignupPage = React.lazy(() => import("./pages/public/SignupPage"));
const OnboardingWizard = React.lazy(() => import("./pages/public/OnboardingWizard"));
const VerifyEmailPage = React.lazy(() => import("./pages/public/VerifyEmailPage"));
const ReactivatePage = React.lazy(() => import("./pages/public/ReactivatePage"));
const ReactivatedPage = React.lazy(() => import("./pages/public/ReactivatedPage"));
const PublicCatalog = React.lazy(() => import("./pages/public/Catalog"));
const PublicItemDetail = React.lazy(() => import("./pages/public/ItemDetail"));
const PublicCheckout = React.lazy(() => import("./pages/public/Checkout"));
import SiteRouter from "./components/SiteRouter";
const PublicInvoicePage = React.lazy(() => import("./pages/public/PublicInvoicePage"));
const PublicQuotePage = React.lazy(() => import("./pages/public/PublicQuotePage"));
const MarketingHome = React.lazy(() => import("./pages/marketing/MarketingHome"));
const FeaturesPage = React.lazy(() => import("./pages/marketing/FeaturesPage"));
const PricingPage = React.lazy(() => import("./pages/marketing/PricingPage"));
const AboutPage = React.lazy(() => import("./pages/marketing/AboutPage"));
const ContactPage = React.lazy(() => import("./pages/marketing/ContactPage"));
const BusinessTypePage = React.lazy(() => import("./pages/marketing/BusinessTypePage"));
const DemoThemeGallery = React.lazy(() => import("./pages/marketing/DemoThemeGallery"));
const DemoDashboard = React.lazy(() => import("./pages/marketing/DemoDashboard"));
import { DemoProvider } from "./contexts/DemoContext";
import { LoadingSpinner } from "./components/common";
const CityLandingPage = React.lazy(() => import("./pages/public/CityLandingPage"));
const RentalsHub = React.lazy(() => import("./pages/marketing/directory/RentalsHub"));
const RentalsListing = React.lazy(() => import("./pages/marketing/directory/RentalsListing"));
const VerifiedOperatorPage = React.lazy(() => import("./pages/marketing/directory/VerifiedOperatorPage"));
const ChangelogPage = React.lazy(() => import("./pages/public/ChangelogPage"));
import "./App.css";

function ProtectedRoute({ children, loginPath = "/admin/login" }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Login />;
  return children;
}

function PublicGuard({ children }) {
  const { configured, loading } = usePublicSite();
  if (loading) return <LoadingSpinner />;
  if (!configured) return <Navigate to="/site-setup" replace />;
  return children;
}

function CustomerGuard({ children }) {
  const { isAuthenticated, loading } = useCustomerAuth();
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/account/login" replace />;
  return children;
}

function PlatformGuard({ children }) {
  const { isAuthenticated, loading } = usePlatformAuth();
  if (loading) return <LoadingSpinner />;
  if (!isAuthenticated) return <Navigate to="/platform/login" replace />;
  return children;
}

// ── Marketing layout language wrappers ────────────────────────────────────
// Wraps MarketingLayout in a MarketingLanguageProvider so public pages
// (Home, Features, Pricing, etc.) can use useT("marketing") + useMarketingPath()
// without duplicating layout code.
function MarketingLayoutEn() {
  return (
    <I18nScope ns="marketing" lang="en">
      <MarketingLayout />
    </I18nScope>
  );
}
function MarketingLayoutEs() {
  return (
    <I18nScope ns="marketing" lang="es">
      <MarketingLayout />
    </I18nScope>
  );
}

function AppRoutes() {
  return (
    <React.Suspense fallback={<LoadingSpinner />}>
      <Routes>
      {/* ─── Marketing Site ─────────────────────────────────────────── */}
      {/* These routes are owned by the platform marketing site.        */}
      {/* In production, microsites live at custom domains (not here).   */}
      <Route element={<MarketingLayoutEn />}>
        <Route path="/" element={<MarketingHome />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/businesses/:type" element={<BusinessTypePage />} />
        <Route path="/demo/themes" element={<DemoThemeGallery />} />
        <Route path="/changelog" element={<ChangelogPage />} />
        <Route path="/terms" element={<LegalPage doc="terms" />} />
        <Route path="/privacy" element={<LegalPage doc="privacy" />} />
        <Route path="/cookies" element={<LegalPage doc="cookies" />} />
        <Route path="/vs" element={<ComparisonsIndex />} />
        <Route path="/vs/:slug" element={<CompetitorComparisonPage />} />
        {/* Operator Directory — the "founding site" hub tenant sites link back to */}
        <Route path="/rentals" element={<RentalsHub />} />
        <Route path="/rentals/:type" element={<RentalsListing />} />
        <Route path="/rentals/:city/:type" element={<RentalsListing />} />
        <Route path="/verified/:subdomain" element={<VerifiedOperatorPage />} />
      </Route>

      {/* ─── Marketing Site — Spanish (SEO Phase 4) ─────────────────── */}
      {/* Same components, wrapped in MarketingLanguageProvider(lang=es).  */}
      <Route element={<MarketingLayoutEs />}>
        <Route path="/es" element={<MarketingHome />} />
        <Route path="/es/features" element={<FeaturesPage />} />
        <Route path="/es/pricing" element={<PricingPage />} />
        <Route path="/es/about" element={<AboutPage />} />
        <Route path="/es/contact" element={<ContactPage />} />
        <Route path="/es/businesses/:type" element={<BusinessTypePage />} />
        <Route path="/es/demo/themes" element={<DemoThemeGallery />} />
        <Route path="/es/changelog" element={<ChangelogPage />} />
        <Route path="/es/vs" element={<ComparisonsIndex />} />
        <Route path="/es/vs/:slug" element={<CompetitorComparisonPage />} />
        {/* Safety net: any unmapped /es/* URL falls back to /es home so
            the ES toggle never leaves the visitor on a blank page. */}
        <Route path="/es/*" element={<Navigate to="/es" replace />} />
      </Route>

      {/* ─── Demo Playground Entry ──────────────────────────────────── */}
      <Route path="/demo/dashboard" element={<DemoDashboard />} />

      {/* ─── Legacy Auth Redirects ─────────────────────────────────── */}
      {/* Old /login and /register routes now redirect to new paths   */}
      <Route path="/login" element={<AuthLoginRedirect />} />
      <Route path="/register" element={<AuthRegisterRedirect />} />

      {/* ─── Signup & Onboarding ────────────────────────────────────── */}
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/reactivate" element={<ReactivatePage />} />
      <Route path="/reactivated" element={<ReactivatedPage />} />
      <Route path="/onboarding" element={<ProtectedRoute loginPath="/signup"><OnboardingWizard /></ProtectedRoute>} />

      {/* ─── Tenant Admin ───────────────────────────────────────────── */}
      <Route path="/admin/login" element={<Login />} />
      <Route path="/admin/forgot-password" element={<TenantForgotPassword />} />
      <Route path="/admin/reset-password" element={<TenantResetPassword />} />
      <Route path="/admin" element={<ProtectedRoute><EditingMicrositeProvider><PlanProvider><FeatureUpgradeProvider><AdminLayout /></FeatureUpgradeProvider></PlanProvider></EditingMicrositeProvider></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="catalog/categories" element={<CategoryList />} />
        <Route path="catalog/items" element={<ItemList />} />
        <Route path="catalog/items/:id" element={<ItemDetail />} />
        <Route path="catalog/packages" element={<PackageList />} />
        <Route path="catalog/packages/:id" element={<PackageDetail />} />
        <Route path="catalog/packages/:id/build" element={<PackageBuilder />} />
        {/* Orders (unified bookings + quotes) */}
        <Route path="orders" element={<OrdersPage />} />
        {/* Keep legacy routes for direct links */}
        <Route path="bookings" element={<BookingList />} />
        <Route path="bookings/new" element={<BookingCreatePage />} />
        <Route path="bookings/:id" element={<BookingDetail />} />
        <Route path="quotes" element={<QuoteList />} />
        <Route path="quotes/new" element={<BookingCreatePage />} />
        <Route path="quotes/:id" element={<QuoteDetail />} />
        {/* Schedule (unified: calendar + dispatch + digest tabs) */}
        <Route path="schedule" element={<SchedulePage />} />
        <Route path="schedule/jobs/:id" element={<JobDetail />} />
        <Route path="staff" element={<StaffPage />} />
        {/* Legacy URLs → unified Schedule page */}
        <Route path="scheduling" element={<Navigate to="/admin/schedule" replace />} />
        <Route path="scheduling/jobs/:id" element={<LegacyJobRedirect />} />
        <Route path="dispatch" element={<Navigate to="/admin/schedule?tab=dispatch" replace />} />
        <Route path="customers" element={<CustomerList />} />
        <Route path="customers/:id" element={<CustomerDetail />} />
        <Route path="financial" element={<FinancialDashboard />} />
        <Route path="marketing" element={<MarketingHub />} />
        <Route path="seo" element={<SEOPage />} />
        <Route path="seo/local-pages" element={<LocalSeoPages />} />
        <Route path="blog" element={<BlogManagement />} />
        <Route path="promotions" element={<PromotionList />} />
                <Route path="promotions/banners" element={<BannerList />} />
                <Route path="promotions/popups" element={<PopupList />} />
        <Route path="reviews" element={<ReviewModeration />} />
        <Route path="inquiries" element={<InquiriesPage />} />
        <Route path="funnels" element={<FunnelList />} />
        <Route path="funnels/:id" element={<FunnelDetail />} />
        <Route path="media" element={<MediaLibrary />} />
        <Route path="waivers" element={<AdminWaivers />} />
        <Route path="themes-v2-preview" element={<ThemesV2Preview />} />
        <Route path="site-builder-v2" element={<SiteBuilderV2Redirect />} />
        <Route path="site-builder-v2/:micrositeId" element={<SiteBuilderV2 />} />
        <Route path="site-builder-v2/:micrositeId/:pageSlug" element={<SiteBuilderV2 />} />
        <Route path="template-gallery" element={<TemplateGallery />} />
        <Route path="community-templates" element={<CommunityTemplateGallery />} />
        <Route path="upgrade" element={<UpgradePlanPage />} />
        <Route path="email-templates" element={<TemplatesPage />} />
        <Route path="invoice-templates" element={<TemplatesPage />} />
        <Route path="templates" element={<TemplatesPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/translation" element={<TranslationSettings />} />
        <Route path="microsites" element={<MicrositesDashboard />} />
        <Route path="microsites/:id" element={<MicrositeSettings />} />
      </Route>

      {/* ─── Platform Admin ─────────────────────────────────────────── */}
      <Route path="/platform/login" element={<PlatformLogin />} />
      <Route path="/platform/forgot-password" element={<PlatformForgotPassword />} />
      <Route path="/platform/reset-password" element={<PlatformResetPassword />} />
      <Route path="/platform" element={<PlatformGuard><PlatformLayout /></PlatformGuard>}>
        <Route index element={<PlatformDashboard />} />
        <Route path="tenants" element={<TenantList />} />
        <Route path="tenants/cleanup" element={<TenantCleanup />} />
        <Route path="tenants/demo" element={<DemoTenants />} />
        <Route path="legal" element={<LegalSettings />} />
        <Route path="tenants/:tenantId" element={<TenantDetail />} />
        <Route path="plans" element={<PlanMatrixEditor />} />
        <Route path="plans/legacy" element={<PlanManagement />} />
        <Route path="themes" element={<ThemeManagement />} />
        <Route path="starter-packs" element={<StarterPackManagement />} />
        <Route path="community" element={<CommunityModerationQueue />} />
        <Route path="integrations" element={<IntegrationsSettings />} />
        <Route path="analytics" element={<PlatformAnalytics />} />
        <Route path="ai-analytics" element={<AIAnalytics />} />
        <Route path="alerts" element={<PlatformAlerts />} />
        <Route path="activity" element={<PlatformActivityFeed />} />
        <Route path="broadcasts" element={<PlatformBroadcasts />} />
        <Route path="website" element={<WebsiteContent />} />
        <Route path="demo-themes" element={<ThemeMarketplace />} />
        <Route path="inbox" element={<PlatformInbox />} />
        <Route path="cohorts" element={<CohortAnalytics />} />
        <Route path="changelog" element={<PlatformChangelog />} />
        <Route path="domains" element={<DomainApprovals />} />
        <Route path="comparisons" element={<ComparisonsManagement />} />
        <Route path="seo/indexnow" element={<IndexNowSettings />} />
        <Route path="i18n" element={<TranslationPlatformSettings />} />
        <Route path="analytics/addons" element={<AddonAnalytics />} />
        <Route path="route-health" element={<RouteHealthPage />} />
        <Route path="seo-setup" element={<SeoSetupChecklistPage />} />
        <Route path="analytics/funnel" element={<MarketingFunnel />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="me" element={<PlatformMePage />} />
        <Route path="audit" element={<PlatformAuditLog />} />
        <Route path="payments" element={<PlatformPayments />} />
      </Route>

      {/* ─── Driver Portal ──────────────────────────────────────────── */}
      <Route path="/driver/login" element={<DriverLogin />} />
      <Route path="/driver" element={<ProtectedRoute><DriverLayout /></ProtectedRoute>}>
        <Route index element={<DriverDayView />} />
        <Route path="all-jobs" element={<DriverJobList />} />
        <Route path="history" element={<DriverHistory />} />
        <Route path="profile" element={<DriverProfile />} />
        <Route path="bookings/:id" element={<DriverJobDetail />} />
        <Route path="jobs/:id" element={<DriverJobDetail />} />
      </Route>

      {/* ─── Customer Portal (theme-aware via redirect) ────────────── */}
      <Route path="/account/login" element={<PublicGuard><CustomerLogin /></PublicGuard>} />
      <Route path="/account/preferences" element={<EmailPreferences />} />
      <Route path="/account" element={<CustomerGuard><PublicGuard><PublicLayout /></PublicGuard></CustomerGuard>}>
        <Route element={<CustomerAccountShell />}>
          <Route index element={<CustomerBookings />} />
          <Route path="bookings/:id" element={<CustomerBookingDetail />} />
          <Route path="quotes" element={<CustomerQuotes />} />
          <Route path="invoices" element={<CustomerInvoices />} />
          <Route path="waivers" element={<CustomerWaivers />} />
          <Route path="profile" element={<CustomerProfile />} />
          <Route path="reviews/new/:bookingId" element={<CustomerReviewSubmit />} />
        </Route>
      </Route>

      {/* ─── Tenant Live Sites (/s/:subdomain/*) ──────────────────── */}
      {/* Clean URLs for tenant sites. In production, these resolve via  */}
      {/* real subdomains (SubdomainRouter). In preview, /s/{subdomain}  */}
      {/* simulates subdomain routing without /preview-* in the URL.     */}
      <Route path="/s/:subdomain/*" element={<SiteRouter />} />

      {/* ─── Legacy /preview & /site redirects ──────────────────────── */}
      {/* The classic /preview-* routes were retired in Phase 6 of the    */}
      {/* Theme Builder Consolidation. /preview and /site/* now route to  */}
      {/* the marketing site so old bookmarks land somewhere useful.       */}
      <Route path="/preview" element={<Navigate to="/" replace />} />
      <Route path="/preview/*" element={<Navigate to="/" replace />} />
      <Route path="/site/*" element={<Navigate to="/" replace />} />
      <Route path="/site" element={<Navigate to="/" replace />} />

      {/* ─── Microsite Preview (dev only) ───────────────────────────── */}
      <Route path="/site-setup" element={<MicrositeSetup />} />
      <Route path="/preview-tenant" element={<TenantPreviewRedirect />} />

      {/* ─── Theme v2 Preview (token-driven) ────────────────────────── */}
      {/* Reads /api/public/themes/token-sets/:slug and renders via the   */}
      {/* <ThemeProvider> + token-aware blocks. The single supported      */}
      {/* theme-preview surface after Phase 6.                              */}
      <Route path="/preview-v2/:slug" element={<React.Suspense fallback={<div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Loading theme preview…</div>}><PreviewV2Page /></React.Suspense>} />

      {/* ─── Phase 6 retirement: legacy /preview-* aliases → token sets ─ */}
      {/* The 9 hardcoded React themes were deleted in Phase 6. Old        */}
      {/* bookmarks and external links now redirect to the closest v2      */}
      {/* token-driven preview so visitors never hit a 404.                */}
      <Route path="/preview-bounce/*"        element={<Navigate to="/preview-v2/sky" replace />} />
      <Route path="/preview-frenzy/*"        element={<Navigate to="/preview-v2/maximalist" replace />} />
      <Route path="/preview-pro/*"           element={<Navigate to="/preview-v2/editorial"  replace />} />
      <Route path="/preview-classic/*"       element={<Navigate to="/preview-v2/warm"       replace />} />
      <Route path="/preview-booth-minimal/*" element={<Navigate to="/preview-v2/glass"      replace />} />
      <Route path="/preview-elegance/*"      element={<Navigate to="/preview-v2/warm"       replace />} />
      <Route path="/preview-boldshop/*"      element={<Navigate to="/preview-v2/editorial"  replace />} />
      <Route path="/preview-partypop/*"      element={<Navigate to="/preview-v2/maximalist" replace />} />
      <Route path="/preview-megarental/*"    element={<Navigate to="/preview-v2/editorial"  replace />} />
      <Route path="/preview-foam"            element={<Navigate to="/" replace />} />
      <Route path="/preview-luxe"            element={<Navigate to="/" replace />} />
      <Route path="/preview-glam"            element={<Navigate to="/" replace />} />
      <Route path="/preview-booth-editorial" element={<Navigate to="/" replace />} />
      <Route path="/preview-booth-luxury"    element={<Navigate to="/" replace />} />
      <Route path="/preview-booth-social"    element={<Navigate to="/" replace />} />
      {/* Legacy booking deep-links → home (tenants book via /s/{sub}/book now) */}
      <Route path="/book-minimal"    element={<Navigate to="/" replace />} />
      <Route path="/book-frenzy"     element={<Navigate to="/" replace />} />
      <Route path="/book-editorial"  element={<Navigate to="/" replace />} />
      <Route path="/book-luxury"     element={<Navigate to="/" replace />} />
      <Route path="/book-social"     element={<Navigate to="/" replace />} />
      <Route path="/book-luxe"       element={<Navigate to="/" replace />} />
      <Route path="/book-rush"       element={<Navigate to="/" replace />} />

      <Route path="/invoice/:token" element={<PublicInvoicePage />} />
      <Route path="/quote/:token" element={<PublicQuotePage />} />

      {/* ─── 404 Catch-all (logs to route-health sentinel) ──────────── */}
      <Route path="*" element={<NotFoundCatcher />} />
      </Routes>
    </React.Suspense>
  );
}

import SubdomainRouter from "./components/SubdomainRouter";

export default function App() {
  return (
    <HelmetProvider>
    <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <RouteErrorBoundary>
      <DemoProvider>
      <I18nProvider>
      <AuthProvider>
        <CustomerAuthProvider>
          <PlatformAuthProvider>
            <PublicSiteProvider>
              <SubdomainRouter>
                <AppRoutes />
                <ThemeLangFab />
              </SubdomainRouter>
            </PublicSiteProvider>
          </PlatformAuthProvider>
        </CustomerAuthProvider>
      </AuthProvider>
      </I18nProvider>
      </DemoProvider>
      </RouteErrorBoundary>
    </BrowserRouter>
    <Toaster position="top-right" richColors closeButton />
    </QueryClientProvider>
    </HelmetProvider>
  );
}
